/** Sticky cart: follow the current main product button across flavour changes. */
(() => {
  'use strict';

  if (window.stickyAddToCart?.version === 'flavour-sync-v1') {
    window.stickyAddToCart.schedule();
    return;
  }

  class StickyAddToCart {
    constructor() {
      this.version = 'flavour-sync-v1';
      this.frame = null;
      this.stickyCart = null;
      this.mainButton = null;
      this.buyButtonWrapper = null;
      this.productContent = null;
      this.isVisible = false;
      this.pendingClick = false;
      this.listeners = [];
      this.schedule = this.schedule.bind(this);

      this.on(window, 'scroll', this.schedule, { passive: true });
      this.on(window, 'resize', this.schedule, { passive: true });
      this.on(window, 'pageshow', this.schedule);
      this.on(document, 'input', this.schedule);
      this.on(document, 'change', this.schedule);

      [
        'product:changed',
        'stitching:productUpdated',
        'shopify:section:load',
        'shopify:section:reorder',
        'sealsubs:price_update',
        'sealsubs:selling_plan_changed',
        'sealsubs:subscription_widget_created'
      ].forEach(name => {
        this.on(document, name, this.schedule);
      });

      this.on(document, 'click', event => this.handleClick(event));

      // Ignore changes inside the sticky bar itself to prevent update loops.
      this.domObserver = new MutationObserver(records => {
        const shouldUpdate = records.some(record => {
          const element = record.target.nodeType === 1
            ? record.target
            : record.target.parentElement;

          return !element?.closest('#sticky-add-to-cart');
        });

        if (shouldUpdate) this.schedule();
      });

      this.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'value']
      });

      this.sync();
    }

    on(target, name, handler, options) {
      target.addEventListener(name, handler, options);

      this.listeners.push(() => {
        target.removeEventListener(name, handler, options);
      });
    }

    schedule() {
      if (this.frame !== null) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    refreshReferences() {
      this.stickyCart = document.getElementById('sticky-add-to-cart');

      this.productContent = document.querySelector(
        '[data-product-main-container]'
      );

      const wrapper = this.productContent?.querySelector(
        '.buy-button__wrapper'
      ) || null;

      this.mainButton = wrapper?.querySelector(
        '.add-to-cart-btn, button[type="submit"], input[type="submit"]'
      ) || null;

      if (wrapper !== this.buyButtonWrapper) {
        this.buyButtonObserver?.disconnect();
        this.resizeObserver?.disconnect();

        this.buyButtonWrapper = wrapper;

        if (wrapper) {
          this.buyButtonObserver = new IntersectionObserver(
            this.schedule,
            { threshold: [0, 0.1, 1] }
          );

          this.buyButtonObserver.observe(wrapper);

          if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.schedule);
            this.resizeObserver.observe(this.productContent);
          }
        }
      }
    }

    getMainPrice() {
      // Read the total already corrected by the quantity selector.
      // Do not read Seal's hidden original prices.
      const host = Array.from(
        this.mainButton?.querySelectorAll('[x-html]') || []
      ).find(element => {
        return (element.getAttribute('x-html') || '').includes(
          'formatMoney(currentPrice)'
        );
      });

      const price = host?.querySelector('.money') ||
        host ||
        this.mainButton?.querySelector('.money');

      return price?.textContent.trim() || '';
    }

    sync() {
      this.refreshReferences();

      if (!this.stickyCart) return;

      const stickyButton = this.stickyCart.querySelector(
        '[data-sticky-add-to-cart]'
      );

      const text = this.stickyCart.querySelector(
        '[data-sticky-button-text]'
      );

      const price = this.getMainPrice();

      const mainText = (
        this.mainButton?.innerText ||
        this.mainButton?.value ||
        ''
      ).replace(/\s+/g, ' ').trim();

      const disabled = !this.mainButton ||
        this.mainButton.disabled ||
        this.mainButton.getAttribute('aria-disabled') === 'true';

      if (stickyButton) {
        stickyButton.disabled = disabled;
        stickyButton.classList.toggle(
          'cursor-not-allowed',
          disabled
        );
      }

      if (text && mainText) {
        const label = !disabled && price
          ? `add to cart - ${price}`
          : mainText;

        if (text.textContent !== label) {
          text.textContent = label;
        }
      }

      this.setText('[data-sticky-price]', price);

      const activePack = this.productContent?.querySelector(
        '[data-pack-option].is-active'
      );

      if (activePack) {
        this.setText(
          '[data-sticky-pack]',
          `${activePack.getAttribute('data-pack-quantity')} cans`
        );

        this.setText(
          '[data-sticky-per-unit]',
          activePack.querySelector('[data-pack-sale-price]')
            ?.textContent.trim() || ''
        );
      }

      const title = this.productContent
        ?.querySelector('.product-title, h1')
        ?.textContent.trim();

      if (title) {
        this.setText(
          '[data-sticky-title]',
          title.split('-')[0].split('XOXO').pop().trim()
        );
      }

      const sourceImage = this.productContent?.querySelector(
        '.product-image img, .featured-image img, ' +
        '.product-media__slider-main-image img'
      );

      const stickyImage = this.stickyCart.querySelector(
        '[data-sticky-image] img'
      );

      if (sourceImage && stickyImage) {
        const src = sourceImage.currentSrc || sourceImage.src;

        if (src && stickyImage.src !== src) {
          stickyImage.removeAttribute('srcset');
          stickyImage.src = src;
        }

        stickyImage.alt = title || sourceImage.alt || '';
      }

      const color = this.productContent
        ?.querySelector('[data-product-color]')
        ?.textContent.trim();

      const rgb = color?.match(/rgba?\(([^)]+)\)/);

      if (rgb) {
        this.stickyCart.setAttribute(
          'data-product-color',
          rgb[1]
        );

        this.stickyCart.style.setProperty(
          '--color-scheme-pdp-background',
          rgb[1]
        );
      }

      this.updateVisibility();
    }

    setText(selector, value) {
      const element = this.stickyCart.querySelector(selector);

      if (element && value && element.textContent !== value) {
        element.textContent = value;
      }
    }

    updateVisibility() {
      const wrapper = this.buyButtonWrapper;
      let show = false;

      if (
        wrapper?.isConnected &&
        wrapper.getClientRects().length &&
        this.mainButton
      ) {
        if (window.innerWidth < 768) {
          // Show only after the current main button has scrolled above view.
          show = wrapper.getBoundingClientRect().bottom <= 0;
        } else if (this.productContent?.isConnected) {
          // Preserve the original desktop threshold.
          show = this.productContent.getBoundingClientRect().bottom <
            window.innerHeight * 0.3;
        }
      }

      this.stickyCart.classList.toggle('is-visible', show);

      if (show !== this.isVisible) {
        this.isVisible = show;

        document.dispatchEvent(
          new CustomEvent('stickyCart:visibilityChanged', {
            detail: { visible: show }
          })
        );
      }
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest(
        '#sticky-add-to-cart [data-sticky-add-to-cart], ' +
        '#sticky-add-to-cart .sticky-cart__scroll-top'
      );

      if (!button) return;

      event.preventDefault();

      if (button.matches('.sticky-cart__scroll-top')) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        return;
      }

      if (this.pendingClick) return;

      this.sync();

      if (
        !this.mainButton ||
        this.mainButton.disabled ||
        this.mainButton.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      this.pendingClick = true;

      // Use the current main button with its selected flavour,
      // quantity and subscription option.
      this.mainButton.click();

      this.clickTimer = window.setTimeout(() => {
        this.pendingClick = false;
      }, 1200);
    }

    destroy() {
      this.listeners.forEach(remove => remove());
      this.domObserver.disconnect();
      this.buyButtonObserver?.disconnect();
      this.resizeObserver?.disconnect();

      if (this.frame !== null) {
        cancelAnimationFrame(this.frame);
      }

      clearTimeout(this.clickTimer);
    }
  }

  function start() {
    window.stickyAddToCart = new StickyAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();